import React, { forwardRef, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    FormGroup,
    FormText,
    Input,
    InputGroup,
    InputGroupAddon,
    Label,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import {
    formatShortBusinessDate,
    getEarliestSelectableDate,
    isBusinessDateBefore,
} from '../../../slices/marketplace/closing-date/utils/closing-date-limits';
import { useStateSelector } from '../../../store/selectors';
import { ProposeClosingDate } from '../../../slices/marketplace/closing-date/thunks/propose-closing-date.thunk';
import { AcceptClosingDateProposal } from '../../../slices/marketplace/closing-date/thunks/accept-closing-date-proposal.thunk';
import { DeclineClosingDateProposal } from '../../../slices/marketplace/closing-date/thunks/decline-closing-date-proposal.thunk';
import { GetTimelineById } from '../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import { GetOpportunity } from '../../../slices/marketplace/opportunity/thunks/get-opportunity.thunk';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';

export type ClosingDateActionMode = 'propose' | 'accept' | 'decline';

const FORM_TITLES: Record<ClosingDateActionMode, string> = {
    propose: 'Propose New Date',
    accept: 'Accept Proposed Date',
    decline: 'Decline Proposed Date',
};

const COMMENT_PLACEHOLDER =
    'A comment is required for every proposal, acceptance, or decline…';
const COMMENT_REQUIRED_MESSAGE =
    'A comment is required for every proposal, acceptance, or decline.';
const DATE_REQUIRED_MESSAGE = 'Please choose a proposed closing date.';

const SHORT_FLOW_OF_FUNDS_TITLE = 'Flow of Funds gets less than 1 week';
const SHORT_FLOW_OF_FUNDS_TEXT =
    'It is highly recommended to allow enough time to prepare the Flow of Funds before closing.';
const CONFIRMATION_BUTTON_TEXTS: Record<
    ClosingDateActionMode,
    { confirm: string; cancel: string }
> = {
    propose: { confirm: 'Propose Anyway', cancel: 'Change Date' },
    accept: { confirm: 'Accept Anyway', cancel: 'Cancel' },
    decline: { confirm: 'Confirm', cancel: 'Cancel' },
};

const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <div
        ref={ref}
        className="btn closing-date-action-form__date-input"
        onClick={onClick}>
        {value || 'Select a date'}
    </div>
));

export interface ClosingDateActionFormProps {
    engagementId: number;
    mode: ClosingDateActionMode;
    proposalId?: string | null;
    proposalDate?: string | null;
    earliestClosingDate?: string | null;
    earliestClosingDateStepName?: string | null;
    flowOfFundsRecommendedClosingDate?: string | null;
    onCancel: () => void;
    onDone: () => void;
}

export const ClosingDateActionForm = ({
    engagementId,
    mode,
    proposalId,
    proposalDate,
    earliestClosingDate,
    earliestClosingDateStepName,
    flowOfFundsRecommendedClosingDate,
    onCancel,
    onDone,
}: ClosingDateActionFormProps) => {
    const dispatch = useDispatch() as any;
    const isSubmitting = useStateSelector(
        (state) => state.closingDateState.isSubmitting
    );
    const currentUserParty = useStateSelector(
        (state) => state.closingDateState.closingDate?.currentUserParty
    );
    const datePickerRef = useRef<any>(null);
    const [proposedDate, setProposedDate] = useState<Date | null>(null);
    const [comment, setComment] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [
        isShortFlowOfFundsConfirmationVisible,
        setIsShortFlowOfFundsConfirmationVisible,
    ] = useState(false);

    const earliestDateSentence = earliestClosingDate
        ? `Earliest possible closing date is ${formatShortBusinessDate(
              earliestClosingDate
          )}: ${earliestClosingDateStepName} needs at least 1 day.`
        : '';
    const isProposalBeforeEarliestDate =
        mode === 'accept' &&
        isBusinessDateBefore(proposalDate, earliestClosingDate);
    const dateChecked = mode === 'propose' ? proposedDate : proposalDate;
    const isFlowOfFundsShort =
        mode !== 'decline' &&
        isBusinessDateBefore(dateChecked, flowOfFundsRecommendedClosingDate);

    const dispatchAction = () => {
        if (mode === 'propose') {
            return dispatch(
                ProposeClosingDate({
                    engagementId,
                    proposedDate: proposedDate as Date,
                    comment: comment.trim(),
                })
            );
        }
        if (mode === 'accept') {
            return dispatch(
                AcceptClosingDateProposal({
                    engagementId,
                    proposalId: proposalId as string,
                    comment: comment.trim(),
                })
            );
        }
        return dispatch(
            DeclineClosingDateProposal({
                engagementId,
                proposalId: proposalId as string,
                comment: comment.trim(),
            })
        );
    };

    const reloadTimeline = () => {
        if (currentUserParty === TimelineParty.Buyer) {
            dispatch(GetOpportunity(engagementId));
            return;
        }
        dispatch(GetTimelineById({ id: engagementId, isTemplate: false }));
    };

    const sendRequest = () => {
        setFieldError('');
        dispatchAction()
            .unwrap()
            .then(() => {
                if (mode === 'accept') {
                    reloadTimeline();
                }
                onDone();
            })
            .catch(() => {});
    };

    const handleSubmit = () => {
        if (!comment.trim()) {
            setFieldError(COMMENT_REQUIRED_MESSAGE);
            return;
        }
        if (mode === 'propose' && !proposedDate) {
            setFieldError(DATE_REQUIRED_MESSAGE);
            return;
        }
        if (isFlowOfFundsShort) {
            setFieldError('');
            setIsShortFlowOfFundsConfirmationVisible(true);
            return;
        }
        sendRequest();
    };

    return (
        <div className="closing-date-action-form">
            <h5 className="closing-date-action-form__title">
                {FORM_TITLES[mode]}
            </h5>
            {mode === 'propose' && (
                <FormGroup>
                    <Label>
                        Proposed Closing Date{' '}
                        <span className="text-danger">*</span>
                    </Label>
                    <InputGroup>
                        <InputGroupAddon
                            addonType="prepend"
                            onClick={() => datePickerRef.current?.setOpen(true)}>
                            <div className="input-group-text">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                            </div>
                        </InputGroupAddon>
                        <DatePicker
                            ref={datePickerRef}
                            selected={proposedDate}
                            onChange={(value: Date) => setProposedDate(value)}
                            minDate={getEarliestSelectableDate(
                                earliestClosingDate
                            )}
                            dateFormat="MM/dd/yyyy"
                            className="form-control"
                            popperProps={{ strategy: 'fixed' }}
                            customInput={<DateControlInput />}
                        />
                    </InputGroup>
                    {earliestDateSentence && (
                        <FormText className="closing-date-action-form__earliest-date">
                            {earliestDateSentence}
                        </FormText>
                    )}
                </FormGroup>
            )}
            {isProposalBeforeEarliestDate && (
                <FormText
                    color="danger"
                    className="closing-date-action-form__earliest-date">
                    {earliestDateSentence}
                </FormText>
            )}
            <FormGroup>
                <Label>
                    Comment <span className="text-danger">*</span>
                </Label>
                <Input
                    type="textarea"
                    rows={3}
                    value={comment}
                    onChange={(e: any) => setComment(e.target.value)}
                    placeholder={COMMENT_PLACEHOLDER}
                />
            </FormGroup>
            {fieldError && <FormText color="danger">{fieldError}</FormText>}
            <div className="closing-date-action-form__buttons">
                <ButtonLoader
                    buttonText="Submit"
                    loaderButtonText="Submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || isProposalBeforeEarliestDate}
                    onClick={handleSubmit}
                    className="btn btn-primary"
                />
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onCancel}>
                    Cancel
                </button>
            </div>
            <ConfirmationModal
                isVisible={isShortFlowOfFundsConfirmationVisible}
                setIsVisible={setIsShortFlowOfFundsConfirmationVisible}
                header={SHORT_FLOW_OF_FUNDS_TITLE}
                confirmationText={SHORT_FLOW_OF_FUNDS_TEXT}
                nextButtonText={CONFIRMATION_BUTTON_TEXTS[mode].confirm}
                cancelButtonText={CONFIRMATION_BUTTON_TEXTS[mode].cancel}
                onConfirm={sendRequest}
            />
        </div>
    );
};
